import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, RotateCcw, BookOpen, AlertCircle } from 'lucide-react';
import PageHero from '../components/PageHero';

// 65 Swedish theory exam questions
const ALL_QUESTIONS = [
  // Hastighet & Avstånd
  { id:1, category:'Hastighet', q:'Vad är den normala hastighetsbegränsningen på motorväg i Sverige?', options:['90 km/h','110 km/h','120 km/h','130 km/h'], correct:2 },
  { id:2, category:'Hastighet', q:'Vad gäller om inget hastighetsmärke finns på en landsväg utanför tättbebyggt område?', options:['50 km/h','70 km/h','90 km/h','110 km/h'], correct:2 },
  { id:3, category:'Hastighet', q:'Hur långt är bromssträckan i torrt väglag vid 90 km/h?', options:['Ungefär 45 m','Ungefär 65 m','Ungefär 90 m','Ungefär 120 m'], correct:2 },
  { id:4, category:'Hastighet', q:'Vad är den normala hastighetsbegränsningen i tättbebyggt område?', options:['30 km/h','50 km/h','70 km/h','90 km/h'], correct:1 },
  { id:5, category:'Hastighet', q:'Vilken hastighet råder på väg med vägmärke "Motortrafikled"?', options:['50 km/h','70 km/h','90 km/h','110 km/h'], correct:2 },
  // Vägmärken
  { id:6, category:'Vägmärken', q:'Vilket vägmärke är rött, triangelformat och varnar för fara?', options:['Påbudsmärke','Förbudsmärke','Varningsmärke','Anvisningsmärke'], correct:2 },
  { id:7, category:'Vägmärken', q:'Vad innebär ett rött, runt märke med vit botten?', options:['Påbud','Förbud','Varning','Information'], correct:1 },
  { id:8, category:'Vägmärken', q:'Vad visar ett blått, runt vägmärke?', options:['Varning','Förbud','Påbud','Information'], correct:2 },
  { id:9, category:'Vägmärken', q:'Vilken form har ett stopp-märke?', options:['Rund','Triangel','Oktogon (8-sidig)','Fyrkant'], correct:2 },
  { id:10, category:'Vägmärken', q:'Vad innebär ett gult, rombformat märke med vit kant?', options:['Förbudsväg','Prioriterad väg (huvudled)','Varningsmärke','Påbudsmärke'], correct:1 },
  // Korsningar
  { id:11, category:'Korsningar', q:'Vilken regel gäller om inget märke finns i en korsning?', options:['Den som kör fortast har företräde','Vänstertrafik gäller','Högerregeln – den som kommer från höger har företräde','Den längre vägen har företräde'], correct:2 },
  { id:12, category:'Korsningar', q:'Du ska svänga vänster. Vad gäller för mötande trafik?', options:['Du har alltid företräde','Mötande trafik har företräde','Du har företräde om du blinkade tidigt','Det beror på vägens bredd'], correct:1 },
  { id:13, category:'Korsningar', q:'Vad ska du göra vid ett "Väjningsplikt"-märke?', options:['Stanna alltid','Sakta in och ge företräde om det behövs','Fortsätta utan att sakta ner','Tuta som varning'], correct:1 },
  { id:14, category:'Korsningar', q:'Hur ska du köra i en cirkulationsplats (rondell)?', options:['Med hög hastighet rakt igenom','Ge företräde åt fordon som redan är inne i rondellen','Du har alltid företräde','Håll till vänster'], correct:1 },
  { id:15, category:'Korsningar', q:'Du ska köra rakt fram. Bil från höger ska svänga höger. Vem har företräde?', options:['Du, du kör rakt fram','Bilen från höger, högerregeln gäller','Ingen har företräde, ni passerar samtidigt','Den bil som kör fortast'], correct:0 },
  // Alkohol & Droger
  { id:16, category:'Alkohol', q:'Vad är den lagliga promillegränsen för bilkörning i Sverige?', options:['0,5 promille','0,2 promille','0,3 promille','0,1 promille'], correct:1 },
  { id:17, category:'Alkohol', q:'Vad är gränsen för grovt rattfylleri i Sverige?', options:['1,0 promille','1,5 promille','0,5 promille','2,0 promille'], correct:0 },
  { id:18, category:'Alkohol', q:'Hur påverkar alkohol körförmågan?', options:['Alkohol förbättrar reaktionsförmågan','Alkohol försämrar omdöme och reaktioner','Alkohol har ingen påverkan under 0,5 promille','Kaffe tar bort alkoholeffekten'], correct:1 },
  { id:19, category:'Alkohol', q:'Kan du bli straffad för rattonykterhet med narkotika i kroppen?', options:['Nej, bara alkohol räknas','Ja, du kan dömas för drograttfylleri','Bara om du kör farligt','Bara om du testar positivt på polisens test'], correct:1 },
  // Trötthet
  { id:20, category:'Trötthet', q:'Hur påverkar trötthet körförmågan?', options:['Det har liten påverkan','Det förbättrar koncentrationen','Det försämrar reaktionsförmågan och ökar olycksrisken','Det påverkar bara unga förare'], correct:2 },
  { id:21, category:'Trötthet', q:'Vad ska du göra om du känner dig sömnig under körning?', options:['Öppna fönstret och fortsätta köra','Stanna och vila','Köra fortare för att nå målet snabbare','Äta en energidryck och fortsätta'], correct:1 },
  { id:22, category:'Trötthet', q:'Hur lång paus rekommenderas minst var 2:a timme vid långa körningar?', options:['5 minuter','10 minuter','15 minuter','45 minuter'], correct:2 },
  // Mörker & Sikt
  { id:23, category:'Mörker', q:'På vilket avstånd ska du kunna se en människa i mörker med helljus?', options:['50 m','100 m','150 m','200 m'], correct:1 },
  { id:24, category:'Mörker', q:'När ska du byta till halvljus från helljus?', options:['Aldrig – helljus är alltid bäst','När du möter eller kör bakom ett fordon','Bara i stadstrafik','Vid 90 km/h eller högre'], correct:1 },
  { id:25, category:'Mörker', q:'Vad innebär bländning?', options:['Att du ser bättre','Att ditt öga tillfälligt inte kan se pga starkt ljus','Att lamporna är trasiga','Att det är dimma'], correct:1 },
  { id:26, category:'Mörker', q:'Vad gör du i dimma?', options:['Kör med helljus','Använder halvljus eller dimbakljus','Slår av alla lampor','Kör mitt på vägen'], correct:1 },
  // Parkering
  { id:27, category:'Parkering', q:'Hur nära en korsning är parkering förbjuden?', options:['5 m','10 m','15 m','20 m'], correct:1 },
  { id:28, category:'Parkering', q:'Hur nära ett övergångsställe är parkering förbjuden?', options:['5 m','10 m','15 m','20 m'], correct:0 },
  { id:29, category:'Parkering', q:'Var är det alltid förbjudet att parkera?', options:['På lekgata','På gångbana','På en envägsgata','I köpcentrum'], correct:1 },
  { id:30, category:'Parkering', q:'Vad innebär "stannförbud"?', options:['Du får inte parkera mer än 3 min','Du får varken stanna eller parkera','Du måste stanna','Du får stanna i 10 min'], correct:1 },
  // Säkerhetsutrustning
  { id:31, category:'Säkerhet', q:'Från vilken ålder krävs bilbälte för alla i bilen?', options:['Det krävs alltid – ingen åldersgräns','Bara för föraren','Bara för framsätespassagerare','Från 15 år'], correct:0 },
  { id:32, category:'Säkerhet', q:'Hur ska ett barn under 135 cm transporteras?', options:['I framsätet utan bälte','Med bilbälte i baksätet','I godkänd bilbarnstol','Med en vuxen i knäet'], correct:2 },
  { id:33, category:'Säkerhet', q:'Vad gör krockkudden om bilen krockar?', options:['Bromsar bilen','Skyddar föraren och passageraren mot hård kollision','Larmar räddningstjänst','Stänger av motorn'], correct:1 },
  { id:34, category:'Säkerhet', q:'Varför är reflexväst viktigt?', options:['Det är modigt','Det gör dig synlig för trafiken','Det skyddar mot kyla','Det krävs alltid i bilen'], correct:1 },
  // Filbyte & Omkörning
  { id:35, category:'Omkörning', q:'Vad ska du göra innan du byter fil?', options:['Tuta','Kontrollera döda vinkeln och speglar, blinka','Bromsa kraftigt','Öka hastigheten'], correct:1 },
  { id:36, category:'Omkörning', q:'Var är det förbjudet att köra om?', options:['På motorväg','Före en backkrön eller kurva med nedsatt sikt','På en rak landsväg','Om den andra bilen kör för sakta'], correct:1 },
  { id:37, category:'Omkörning', q:'I vilken fil ska du normalt köra på en tvåfilig väg i Sverige?', options:['Vänster fil','Höger fil','Mittfil','Det spelar ingen roll'], correct:1 },
  // Miljö & Ekonomi
  { id:38, category:'Miljö', q:'Vad minskar en bils bränsleförbrukning mest?', options:['Luftkonditionering','Jämn, lugn körning och rätt däcktryck','Automatväxellåda','Taklucka'], correct:1 },
  { id:39, category:'Miljö', q:'Vilken körning är mest miljövänlig?', options:['Hög hastighet på motorväg','Lugn körning med tidiga växlingsbyten','Stanna och starta ofta i stadsköring','Motorvärmare alltid på'], correct:1 },
  // Fordon & Teknik
  { id:40, category:'Teknik', q:'Vad är ABS (Anti-lock Braking System)?', options:['Automatisk bromsassistans som hindrar hjulen från att låsa sig','Ett system som ökar farten','Automatisk styrning','Krockkuddar'], correct:0 },
  { id:41, category:'Teknik', q:'Vad innebär ESP (Electronic Stability Program)?', options:['Förhindrar att bilen sladdar','Ökar motorns effekt','Mäter bränslesnålhet','Styr radion'], correct:0 },
  { id:42, category:'Teknik', q:'Hur ofta rekommenderas däckskifte i Sverige?', options:['Var 5:e år','Vid vinterns inbrott och vårens slut','Varje år','Bara om de är slitna'], correct:1 },
  { id:43, category:'Teknik', q:'Vad ska mönsterdjupet vara minst på sommardäck?', options:['1 mm','1,6 mm','3 mm','4 mm'], correct:1 },
  { id:44, category:'Teknik', q:'Vad ska mönsterdjupet vara minst på vinterdäck?', options:['1,6 mm','3 mm','4 mm','5 mm'], correct:1 },
  // Körfält & Position
  { id:45, category:'Position', q:'Hur ska du positionera bilen inför en vänstersväng?', options:['Håll dig till höger','Kör upp till mittlinjen','Kör i vänster fil om det finns','Stannar mitt i korsningen'], correct:2 },
  { id:46, category:'Position', q:'Vad innebär "körfältsval" på motorväg?', options:['Välj körfält slumpmässigt','Håll dig i höger fil och använd vänster för omkörning','Alltid köra i mittfilen','Alltid köra i vänster fil'], correct:1 },
  // Gående & Cyklister
  { id:47, category:'Gående', q:'Har du som bilist företräde framför en fotgängare på ett övergångsställe?', options:['Ja, om du kör rakt fram','Nej, fotgängaren har alltid företräde','Ja, om du blinkade','Det beror på trafiksignalerna'], correct:1 },
  { id:48, category:'Gående', q:'Vad ska du göra när en buss stannar och dörrarna öppnas vid hållplats?', options:['Köra om bussen snabbt','Vara beredd på att fotgängare korsar vägen','Tuta','Öka hastigheten'], correct:1 },
  { id:49, category:'Gående', q:'Vad gäller för cykelöverfart?', options:['Cyklister har alltid företräde','Du ska visa hänsyn och sänka hastigheten','Du har alltid företräde som bilist','Cyklister är förbjudna att korsa'], correct:1 },
  // Motorväg
  { id:50, category:'Motorväg', q:'Vad gäller vid påfart på motorväg?', options:['Fordon på motorvägen väjer','Du anpassar hastigheten och smälter in i trafiken','Du har alltid rätt att köra in','Kör i vänster fil direkt'], correct:1 },
  { id:51, category:'Motorväg', q:'Vad är lägsta tillåtna hastighet på motorväg?', options:['50 km/h','60 km/h','70 km/h','80 km/h'], correct:1 },
  { id:52, category:'Motorväg', q:'Är det tillåtet att backa på motorväg?', options:['Ja, om det är nödvändigt','Ja, om ingen annan ser','Nej, det är förbjudet','Bara på axeln'], correct:2 },
  // Olycka & Nöd
  { id:53, category:'Olycka', q:'Vad gör du FÖRST vid en trafikolycka med skadade?', options:['Ringer försäkringsbolaget','Säkrar platsen och ringer 112','Fotar för försäkringen','Väntar på att andra ska hjälpa till'], correct:1 },
  { id:54, category:'Olycka', q:'Vad betyder larmnumret 112?', options:['Polisen','Ambulans','SOS Alarm – alla räddningstjänster','Vägassistans'], correct:2 },
  { id:55, category:'Olycka', q:'Har du skyldighet att stanna vid olycka även om du inte är inblandad?', options:['Nej','Ja, du har hjälpplikt','Bara om du är sjukvårdare','Bara om det är allvarliga skador'], correct:1 },
  // Avstånd & Reaktion
  { id:56, category:'Avstånd', q:'Vad är "2-sekundersregeln"?', options:['Håll 2 sekunders tidsgap till framförvarande','Kör 2 km/h under hastighetsbegränsningen','Stanna 2 sekunder vid korsning','Kontrollera spegeln var 2:a sekund'], correct:0 },
  { id:57, category:'Avstånd', q:'Hur lång är en genomsnittlig reaktionssträcka vid 90 km/h?', options:['Ungefär 15 m','Ungefär 25 m','Ungefär 50 m','Ungefär 75 m'], correct:1 },
  // Väder & Väglag
  { id:58, category:'Väder', q:'Hur ska du anpassa hastigheten vid halt väglag?', options:['Samma hastighet, ABS bromsar','Lägre hastighet och längre bromssträcka','Högre hastighet så hjulen inte låser sig','Ingen anpassning krävs'], correct:1 },
  { id:59, category:'Väder', q:'Vad gör du om bilen börjar sladda?', options:['Bromsar hårt','Styr mot sladden och gas försiktigt','Styr bort från sladden','Väljer neutralläge på växeln'], correct:1 },
  { id:60, category:'Väder', q:'Vad gäller angående vinterdäck i Sverige?', options:['Frivilligt – ingen lagstiftning','Obligatoriskt 1 dec – 31 mars','Vinterdäck är obligatoriska vid vinterväglag (under 0°C eller snö/is)','Bara för personbilar under 3,5 ton'], correct:2 },
  // Lagar & Regler
  { id:61, category:'Lagar', q:'Vad är körkortstillståndet giltigt i (för B-körkort)?', options:['5 år','10 år','15 år','Tills vidare'], correct:3 },
  { id:62, category:'Lagar', q:'Är det obligatoriskt att ha med körkortet i bilen?', options:['Ja, alltid','Nej, men du ska kunna uppvisa det inom 7 dagar','Ja, polisen kan beslagta bilen','Bara vid långa resor'], correct:1 },
  { id:63, category:'Lagar', q:'Vad kan hända om du kör med ett ogiltigt körkort?', options:['Du får en varning','Du kan dömas och körkortet återkallas','Inget, det är bara en formalitet','Du betalar en parkeringsavgift'], correct:1 },
  // Djur & Speciella Situationer
  { id:64, category:'Övrigt', q:'Vad ska du göra om ett djur springer ut framför bilen?', options:['Alltid köra om det – djuret undviker dig','Bromsa kontrollerat och stanna om möjligt','Alltid svänga bort – också om det är farligt','Tuta för att skrämma djuret'], correct:1 },
  { id:65, category:'Övrigt', q:'Vad ska du göra om du kör in i en tunnel och det börjar brinna?', options:['Stanna vid sidan och springa ut ur tunneln','Kör ut ur tunneln om möjligt, annars stanna och utrymm fordonet','Stanna mitt i tunneln och vänta','Öka hastigheten'], correct:1 },
];

const PASS_SCORE = 52;
const TOTAL = 65;

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function Teoriprov() {
  const [questions] = useState(() => shuffleArray(ALL_QUESTIONS).slice(0, TOTAL));
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(45 * 60);
  const [started, setStarted] = useState(false);
  const [currentQ, setCurrentQ] = useState(0);

  useEffect(() => {
    if (!started || submitted) return;
    const interval = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { setSubmitted(true); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [started, submitted]);

  const handleAnswer = (qId, optIdx) => {
    if (submitted) return;
    setAnswers(a => ({ ...a, [qId]: optIdx }));
  };

  const handleSubmit = () => {
    if (Object.keys(answers).length < TOTAL) {
      const unanswered = questions.filter(q => answers[q.id] === undefined).length;
      if (!window.confirm(`Du har ${unanswered} obesvarade frågor. Vill du ändå skicka in?`)) return;
    }
    setSubmitted(true);
  };

  const score = submitted ? questions.filter(q => answers[q.id] === q.correct).length : 0;
  const passed = score >= PASS_SCORE;

  const formatTime = (s) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;
  const answeredCount = Object.keys(answers).length;

  if (!started) return (
    <div>
      <PageHero title="Gratis" accent="Teoriprov" desc="Öva inför ditt körkortsteoriprov – 65 frågor precis som det riktiga provet." breadcrumbs={[{label:'Teoriprov'}]}/>
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-5"><BookOpen size={28} className="text-red-600"/></div>
          <h2 className="font-display font-black text-3xl text-gray-900 uppercase mb-2">Körkortsteoriprov</h2>
          <p className="text-gray-500 mb-6">Testet simulerar ett riktigt teoriprov. Du har <strong>45 minuter</strong> på dig att svara på <strong>65 frågor</strong>.</p>
          <div className="grid grid-cols-3 gap-4 mb-8">
            {[['65','Frågor'],['45','Minuter'],['52','Godkänt (av 65)']].map(([v,l])=>(
              <div key={l} className="bg-gray-50 rounded-xl p-4">
                <div className="font-display font-black text-3xl text-red-600">{v}</div>
                <div className="text-xs text-gray-500 mt-0.5">{l}</div>
              </div>
            ))}
          </div>
          <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-6 text-left">
            <h3 className="font-bold text-red-700 text-sm mb-2 flex items-center gap-2"><AlertCircle size={14}/>Inför provet</h3>
            <ul className="text-sm text-red-600 space-y-1">
              <li>• Varje fråga har 4 svarsalternativ – välj det bästa.</li>
              <li>• Du måste ha <strong>minst 52 rätt</strong> för att klara.</li>
              <li>• Du kan granska dina svar i slutet.</li>
              <li>• Tidern räknas ner – försök svara på alla frågor.</li>
            </ul>
          </div>
          <button onClick={() => setStarted(true)} className="btn-red w-full justify-center text-base py-4">
            Starta Teoriprov →
          </button>
          <p className="text-xs text-gray-400 mt-4">Gratis övningsprov från Alfa Trafikskola AB, Kista</p>
        </div>
      </div>
    </div>
  );

  if (submitted) return (
    <div>
      <PageHero title="Provresultat" breadcrumbs={[{label:'Teoriprov',to:'/teoriprov'}]}/>
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Result card */}
        <div className={`rounded-2xl p-8 mb-8 text-center ${passed ? 'bg-green-50 border-2 border-green-400' : 'bg-red-50 border-2 border-red-400'}`}>
          <div className="text-5xl mb-3">{passed ? '🎉' : '📚'}</div>
          <h2 className="font-display font-black text-4xl uppercase mb-2">{passed ? 'Godkänt!' : 'Ej Godkänt'}</h2>
          <div className="font-display font-black text-6xl text-gray-900 mb-1">{score}<span className="text-2xl text-gray-400">/{TOTAL}</span></div>
          <p className={`font-semibold ${passed ? 'text-green-700' : 'text-red-700'}`}>{passed ? `Grattis! ${score} rätt – du klarade provet!` : `${score} rätt. Du behöver ${PASS_SCORE} för att klara. Fortsätt öva!`}</p>
          {!passed && (
            <div className="mt-4">
              <Link to="/teorilektioner" className="btn-red inline-flex">Boka Teorilektion</Link>
            </div>
          )}
        </div>

        {/* Category breakdown */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8">
          <h3 className="font-bold text-gray-900 mb-4">Resultat per kategori</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[...new Set(questions.map(q => q.category))].map(cat => {
              const catQs = questions.filter(q => q.category === cat);
              const catCorrect = catQs.filter(q => answers[q.id] === q.correct).length;
              const pct = Math.round(catCorrect / catQs.length * 100);
              return (
                <div key={cat} className="bg-gray-50 rounded-xl p-3">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{cat}</div>
                  <div className="font-display font-black text-xl text-gray-900">{catCorrect}/{catQs.length}</div>
                  <div className="h-1.5 bg-gray-200 rounded-full mt-2 overflow-hidden">
                    <div className={`h-full rounded-full ${pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-yellow-400' : 'bg-red-500'}`} style={{width:`${pct}%`}}/>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* All questions review */}
        <h3 className="font-display font-black text-2xl text-gray-900 uppercase mb-4">Genomgång av alla frågor</h3>
        <div className="space-y-4">
          {questions.map((q, idx) => {
            const userAns = answers[q.id];
            const isCorrect = userAns === q.correct;
            const unanswered = userAns === undefined;
            return (
              <div key={q.id} className={`rounded-xl border p-5 ${isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className={`shrink-0 mt-0.5 ${isCorrect ? 'text-green-500' : 'text-red-500'}`}>
                    {isCorrect ? <CheckCircle size={18}/> : <XCircle size={18}/>}
                  </div>
                  <div>
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">{idx+1}. {q.category}</span>
                    <p className="font-semibold text-gray-900 mt-0.5">{q.q}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-7">
                  {q.options.map((opt, i) => (
                    <div key={i} className={`text-sm px-3 py-2 rounded-lg flex items-center gap-2 ${i === q.correct ? 'bg-green-100 text-green-800 font-semibold' : i === userAns && !isCorrect ? 'bg-red-100 text-red-700 line-through' : 'text-gray-500 bg-white/50'}`}>
                      <span className={`w-5 h-5 rounded-full text-xs flex items-center justify-center shrink-0 font-bold ${i === q.correct ? 'bg-green-500 text-white' : i === userAns && !isCorrect ? 'bg-red-400 text-white' : 'bg-gray-200 text-gray-500'}`}>
                        {String.fromCharCode(65+i)}
                      </span>
                      {opt}
                    </div>
                  ))}
                </div>
                {unanswered && <p className="text-xs text-orange-500 mt-2 ml-7">⚠️ Ej besvarad</p>}
              </div>
            );
          })}
        </div>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <button onClick={() => window.location.reload()} className="btn-red"><RotateCcw size={14}/> Gör om provet</button>
          <Link to="/korlektioner" className="btn-red-outline"><BookOpen size={14}/> Boka Körlektion</Link>
          {!passed && <Link to="/teorilektioner" className="btn-red"><BookOpen size={14}/> Boka Teorilektion</Link>}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {/* Sticky top bar */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`font-mono font-bold text-lg ${timeLeft < 300 ? 'text-red-600' : 'text-gray-700'}`}>
              <Clock size={14} className="inline mr-1.5"/>{formatTime(timeLeft)}
            </div>
            <div className="hidden sm:block text-sm text-gray-400">
              {answeredCount}/{TOTAL} besvarade
            </div>
          </div>
          {/* Progress */}
          <div className="flex-1 max-w-xs">
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-red-600 rounded-full transition-all duration-300" style={{width:`${answeredCount/TOTAL*100}%`}}/>
            </div>
          </div>
          <button onClick={handleSubmit} className="btn-red-sm">
            Rätta <CheckCircle size={13}/>
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Quick navigation */}
        <div className="flex flex-wrap gap-1.5 mb-8">
          {questions.map((q, idx) => (
            <button key={q.id} onClick={() => setCurrentQ(idx)}
              className={`w-8 h-8 text-xs font-bold rounded-lg transition-colors ${answers[q.id] !== undefined ? 'bg-red-600 text-white' : currentQ === idx ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
              {idx+1}
            </button>
          ))}
        </div>

        {/* Questions */}
        <div className="space-y-6">
          {questions.map((q, idx) => (
            <div key={q.id} id={`q-${idx}`} className={`bg-white rounded-2xl border p-6 transition-all ${answers[q.id] !== undefined ? 'border-red-200' : 'border-gray-100'} ${currentQ === idx ? 'ring-2 ring-red-300' : ''}`}>
              <div className="flex items-start gap-3 mb-4">
                <span className="shrink-0 w-8 h-8 bg-red-600 text-white text-sm font-black rounded-lg flex items-center justify-center">{idx+1}</span>
                <div className="flex-1">
                  <span className="text-xs font-bold text-red-400 uppercase tracking-wider block mb-1">{q.category}</span>
                  <p className="font-semibold text-gray-900">{q.q}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-11">
                {q.options.map((opt, i) => (
                  <button key={i} type="button" onClick={() => { handleAnswer(q.id, i); setCurrentQ(idx+1); }}
                    className={`text-left px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all flex items-center gap-3 ${answers[q.id] === i ? 'border-red-600 bg-red-50 text-red-700' : 'border-gray-100 bg-gray-50 hover:border-red-300 hover:bg-red-50 text-gray-700'}`}>
                    <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center shrink-0 font-black ${answers[q.id] === i ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                      {String.fromCharCode(65+i)}
                    </span>
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <button onClick={handleSubmit} className="btn-red text-base px-10 py-4">
            <CheckCircle size={16}/> Rätta Provet ({answeredCount}/{TOTAL} besvarade)
          </button>
        </div>
      </div>
    </div>
  );
}
